"use client";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faAnglesUpDown,
  faGear,
  faShieldExclamation,
  faSignOut,
} from "@nnr/fontawesome-sharp-light/icons/sharp/light";
import { useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { toast } from "sonner";
import { signOut, useAuthDetails } from "../../../context/auth-context";
import { getInitials } from "../../../lib/string";
import { cn } from "../../../lib/utils";
import { RoleGate } from "../../gate/RoleGate";

export function SidebarUser() {
  const { isMobile, open } = useSidebar();
  const authDetails = useAuthDetails();
  const initials = getInitials(authDetails?.name ?? "");
  const queryClient = useQueryClient();

  async function handleSignOut() {
    await signOut();
    queryClient.clear();
    toast.success("You have been logged out.");
  }

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className={cn(
                "data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground",
                { "rounded-full": !open },
              )}
            >
              <Avatar className="h-8 w-8">
                <AvatarImage
                  src={authDetails?.photo_url ?? undefined}
                  alt={authDetails?.name}
                />
                <AvatarFallback>{initials}</AvatarFallback>
              </Avatar>
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-semibold">
                  {authDetails?.name}
                </span>
                <span className="truncate text-xs">{authDetails?.email}</span>
              </div>
              <FontAwesomeIcon icon={faAnglesUpDown} className="ml-auto" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
            side={isMobile ? "bottom" : "right"}
            align="end"
            sideOffset={4}
          >
            <DropdownMenuLabel className="p-0 font-normal">
              <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                <Avatar className="h-8 w-8">
                  <AvatarImage
                    src={authDetails?.photo_url ?? undefined}
                    alt={authDetails?.name}
                  />
                  <AvatarFallback>{initials}</AvatarFallback>
                </Avatar>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-semibold">
                    {authDetails?.name}
                  </span>
                  <span className="truncate text-xs">{authDetails?.email}</span>
                </div>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <RoleGate systemRoles="super">
              <DropdownMenuItem>
                <Link to="/admin" className="flex flex-1 items-center gap-2">
                  <FontAwesomeIcon icon={faShieldExclamation} />
                  Super Admin
                </Link>
              </DropdownMenuItem>
            </RoleGate>
            <RoleGate systemRoles={["super"]}>
              <DropdownMenuSeparator />
            </RoleGate>
            <DropdownMenuItem>
              <Link to="/u/settings" className="flex flex-1 items-center gap-2">
                <FontAwesomeIcon icon={faGear} />
                Settings
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem onClick={handleSignOut}>
              <FontAwesomeIcon icon={faSignOut} />
              Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
